import React, { useState, useMemo, useEffect } from 'react';
import { formatCurrency, formatDateShort } from '../config/api';
import { isPartial, paidAmountOf, amountDueOf, refundDueOf } from '../billPayment';
import './BatchBillPrintModal.css';

// So tien khong kem ky hieu tien te - dung cho cac cot trong bang.
// Hoa don chi dat ky hieu "đ" o dong Tong cong de bang so gon va thang cot.
const formatPlain = (val) => new Intl.NumberFormat('vi-VN').format(Number(val) || 0);

// Bo dau, ha chu thuong, bo ky tu khong phai chu/so -> de so sanh ten
const normalizeName = (str) => (str || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Danh sach vi banh co trung lap voi ten san pham khong?
 * Don banh le co dang: ten "Bánh lẻ: Bánh Dẻo Cốm Dừa",
 * vi banh "1. Bánh Dẻo Cốm Dừa" -> lap lai y nguyen, chi lam
 * moi dong cao gap doi ma khong them thong tin nao.
 */
const isRedundantSubList = (itemName, subs) => {
    if (!Array.isArray(subs) || subs.length !== 1) return false;
    const parent = normalizeName(itemName);
    const child = normalizeName(subs[0] && subs[0].name);
    if (!child) return true;
    return parent.includes(child) || child.includes(parent);
};

/**
 * Do dac cua hoa don, chon theo so dong hang.
 * Hoa don ban banh le co the len 8-14 dong; neu de nguyen co giay
 * thi phan Tong cong + ma QR bi day ra ngoai va mat khi in.
 */
// 8 dong la so dong vua mot o giay A4 chia 4, o che do ultra-dense,
// SAU KHI da danh du cho cho ma QR dat chuan 25mm.
// Ma QR khong nhuong cho - dong hang nhuong.
const MAX_ITEM_ROWS = 8;

/**
 * Do dac tinh theo CHIEU CAO thuc te, khong chi theo so mon:
 * mot don 3 hop banh moi hop liet ke 4 vi cao hon han mot don
 * 3 banh le khong co vi. Moi mon tinh 1 diem, cong them 1 diem
 * cho moi ~3 vi banh se in ra.
 */
const getDensity = (items) => {
    const list = Array.isArray(items) ? items : [];
    const score = list.reduce((acc, it) => {
        const subs = Array.isArray(it.items) ? it.items : [];
        const printed = isRedundantSubList(it.name, subs) ? 0 : subs.length;
        return acc + 1 + Math.ceil(printed / 3);
    }, 0);
    if (score >= 8) return 'ultra-dense';   // bo vi banh, dong sat nhau
    if (score >= 5) return 'dense';         // thu nho, vi banh 1 dong
    return 'normal';
};

export default function BatchBillPrintModal({
    isOpen,
    onClose,
    bills = [],
    initialDate = 'all',
    sepayConfig = {
        bankShortName: 'MB',
        accountNumber: '0398176703',
        accountHolder: 'VU THE HAI'
    },
    singleBill = null
}) {
    // Delivery dates available in bills
    const availableDates = useMemo(() => {
        const dates = Array.from(
            new Set(
                bills
                    .map(b => (b.delivery_date ? b.delivery_date.split('T')[0] : null))
                    .filter(Boolean)
            )
        ).sort();
        return dates;
    }, [bills]);

    const [selectedDate, setSelectedDate] = useState(initialDate || 'all');
    const [statusFilter, setStatusFilter] = useState('undelivered'); // 'undelivered', 'active_all', 'paid', 'unpaid', 'make', 'shipping', 'delivered', 'all'
    const [layout, setLayout] = useState('grid4'); // 'grid4' (4 bills/A4 - 2x2) or 'grid2' (2 bills/A4)
    const [colorMode, setColorMode] = useState('bw'); // 'bw' (Đen trắng tương phản cao) or 'color' (Màu)
    const [qrMode, setQrMode] = useState('unpaid_only'); // 'unpaid_only' (Chỉ hiện QR khi chưa thanh toán / COD, đã thanh toán ra con dấu), 'always_vietqr', 'lookup'
    const [showQr, setShowQr] = useState(true);
    const [showSubCakes, setShowSubCakes] = useState(true);
    const [showCutLines, setShowCutLines] = useState(true);
    const [showCustomerNote, setShowCustomerNote] = useState(true);
    const [selectedBillIds, setSelectedBillIds] = useState([]);

    // Add has-print-bills class to body when modal is open to isolate print styles
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('has-print-bills');
        } else {
            document.body.classList.remove('has-print-bills');
        }
        return () => {
            document.body.classList.remove('has-print-bills');
        };
    }, [isOpen]);

    // Update selected date whenever modal opens with an initialDate
    useEffect(() => {
        if (isOpen) {
            if (singleBill) {
                setSelectedBillIds([singleBill.id]);
            } else if (initialDate) {
                setSelectedDate(initialDate);
            }
        }
    }, [isOpen, initialDate, singleBill]);

    // Filter bills according to selectedDate & statusFilter
    const filteredBills = useMemo(() => {
        if (singleBill) return [singleBill];

        return bills.filter(b => {
            // Filter by date
            if (selectedDate !== 'all') {
                const bDate = b.delivery_date ? b.delivery_date.split('T')[0] : 'Chưa định ngày';
                if (bDate !== selectedDate) return false;
            }

            // Filter by status
            const status = (b.status || '').toLowerCase().trim();
            const isPaid = ((b.paymentStatus || '').toLowerCase() === 'paid' || status.includes('đã thanh toán')) && !isPartial(b);
            const isDelivered = status === 'đã giao hàng' || status === 'đã giao' || status.includes('đã giao') || status.includes('da giao');

            if (statusFilter === 'undelivered') {
                // All active undelivered (exclude cancelled & delivered)
                if (status.includes('hủy') || status.includes('huy') || isDelivered) return false;
            } else if (statusFilter === 'active_all') {
                // All active (exclude cancelled, include delivered)
                if (status.includes('hủy') || status.includes('huy')) return false;
            } else if (statusFilter === 'delivered') {
                if (!isDelivered || status.includes('hủy')) return false;
            } else if (statusFilter === 'paid') {
                if (!isPaid || isDelivered) return false;
            } else if (statusFilter === 'unpaid') {
                if (isPaid || status.includes('hủy') || isDelivered) return false;
            } else if (statusFilter === 'make') {
                if ((!status.includes('làm') && !status.includes('bánh')) || isDelivered) return false;
            } else if (statusFilter === 'shipping') {
                if (!status.includes('đang giao')) return false;
            }

            return true;
        });
    }, [bills, selectedDate, statusFilter, singleBill]);

    // Keep all filtered bills selected by default when filter changes
    useEffect(() => {
        if (!singleBill) {
            setSelectedBillIds(filteredBills.map(b => b.id));
        }
    }, [filteredBills, singleBill]);

    // Bills chosen for final printing
    const billsToPrint = useMemo(() => {
        if (singleBill) return [singleBill];
        return filteredBills.filter(b => selectedBillIds.includes(b.id));
    }, [filteredBills, selectedBillIds, singleBill]);

    // Chunk bills into pages (4 per page for grid4, 2 per page for grid2)
    const pageSize = layout === 'grid4' ? 4 : 2;
    const pages = useMemo(() => {
        const result = [];
        for (let i = 0; i < billsToPrint.length; i += pageSize) {
            const chunk = billsToPrint.slice(i, i + pageSize);
            // Pad chunk with nulls to retain grid slots
            while (chunk.length < pageSize) {
                chunk.push(null);
            }
            result.push(chunk);
        }
        return result;
    }, [billsToPrint, pageSize]);

    // Handle check all / uncheck all
    const handleToggleAll = () => {
        if (selectedBillIds.length === filteredBills.length) {
            setSelectedBillIds([]);
        } else {
            setSelectedBillIds(filteredBills.map(b => b.id));
        }
    };

    const handleToggleBill = (id) => {
        setSelectedBillIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handlePrint = () => {
        window.print();
    };

    if (!isOpen) return null;

    // Helper to parse items array safely
    const parseItems = (rawItems) => {
        if (!rawItems) return [];
        if (Array.isArray(rawItems)) return rawItems;
        try {
            const parsed = JSON.parse(rawItems);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    };

    // Generate QR details according to qrMode
    const getQrData = (bill) => {
        const isPaid = ((bill.paymentStatus || '').toLowerCase() === 'paid' || (bill.status || '').toLowerCase().includes('đã thanh toán')) && !isPartial(bill);
        const isDeposit = bill.paymentMethod === 'deposit_30';

        const bank = sepayConfig.bankShortName || 'MB';
        const acc = sepayConfig.accountNumber || '0398176703';
        const orderCode = bill.orderCode || `TT${bill.id || ''}`;
        const cleanOrderCode = orderCode.replace(/^#+/, '').trim();

        // In unpaid_only mode, paid bills do not show a QR footer.
        if (qrMode === 'unpaid_only' && isPaid) {
            return null;
        }

        if (qrMode === 'lookup') {
            // Web Order Tracking QR Code
            const queryParam = encodeURIComponent(cleanOrderCode);
            const targetUrl = `https://order.cookielabs.vn/my-orders?query=${queryParam}`;
            // size lon + margin 4 module (dung chuan quiet zone) de in ra quet duoc
            const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=4&ecc=M&data=${encodeURIComponent(targetUrl)}`;

            return {
                type: 'lookup',
                url: qrUrl,
                title: 'Tra cứu đơn',
                subtitle: `#${cleanOrderCode}`,
            };
        }

        // Banking VietQR (MBBank NAPAS 247)
        let amount = Number(bill.totalAmount) || 0;
        if (isPartial(bill)) {
            // Paid before a flavour change raised the price: the QR collects only the difference.
            amount = amountDueOf(bill);
        } else if (isDeposit && !isPaid) {
            const dep = Math.round(amount * 0.3);
            amount = Math.max(0, amount - dep);
        } else if (isPaid) {
            amount = 0;
        }

        const amountParam = amount > 0 ? `&amount=${amount}` : '';
        const qrUrl = `https://qr.sepay.vn/img?acc=${encodeURIComponent(acc)}&bank=${encodeURIComponent(bank)}${amountParam}&des=${encodeURIComponent(cleanOrderCode)}&template=qr_only`;

        return {
            type: 'vietqr',
            url: qrUrl,
            title: 'VietQR MBBank',
            subtitle: `STK: ${acc}`,
        };
    };

    return (
        <div className="batch-print-modal-overlay" onClick={onClose}>
            <div className="batch-print-modal-dialog" onClick={(e) => e.stopPropagation()}>
                {/* MODAL HEADER */}
                <div className="batch-print-modal-header no-print">
                    <div className="batch-print-header-title">
                        <i className="fa-solid fa-print" style={{ fontSize: '20px', color: '#142C54' }}></i>
                        <h3>In Hóa Đơn &amp; Phiếu Giao Hàng (Khổ A4 Cắt Rời)</h3>
                        <span className="batch-print-header-badge">
                            {billsToPrint.length} đơn hàng · {pages.length} tờ A4
                        </span>
                    </div>
                    <button type="button" className="batch-print-close-btn" onClick={onClose} title="Đóng cửa sổ">
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </div>

                {/* TOOLBAR CONTROLS */}
                <div className="batch-print-toolbar no-print">
                    <div className="batch-print-filter-group">
                        {/* Date selector */}
                        {!singleBill && (
                            <div className="batch-print-field">
                                <i className="fa-regular fa-calendar-days" style={{ color: '#142C54' }}></i>
                                <span>Ngày giao:</span>
                                <select
                                    className="batch-print-select"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                >
                                    <option value="all">Toàn bộ các ngày ({bills.length} đơn)</option>
                                    {availableDates.map(d => {
                                        const count = bills.filter(b => (b.delivery_date || '').startsWith(d)).length;
                                        return (
                                            <option key={d} value={d}>
                                                Ngày {formatDateShort(d)} ({count} đơn)
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        )}

                        {/* Status selector */}
                        {!singleBill && (
                            <div className="batch-print-field">
                                <span>Trạng thái:</span>
                                <select
                                    className="batch-print-select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="undelivered">Tất cả đơn chưa giao (Mặc định)</option>
                                    <option value="paid">Chỉ đơn ĐÃ thanh toán (chưa giao)</option>
                                    <option value="unpaid">Chờ thanh toán / COD (chưa giao)</option>
                                    <option value="make">Đang làm bánh</option>
                                    <option value="shipping">Đang giao hàng</option>
                                    <option value="delivered">Chỉ đơn ĐÃ giao hàng</option>
                                    <option value="active_all">Toàn bộ đơn chưa hủy (gồm đã giao)</option>
                                    <option value="all">Toàn bộ trạng thái (kể cả hủy)</option>
                                </select>
                            </div>
                        )}

                        {/* Layout Toggle */}
                        <div className="batch-print-field">
                            <span>Khổ in:</span>
                            <div className="batch-print-toggle-pills">
                                <button
                                    type="button"
                                    className={`batch-print-toggle-btn ${layout === 'grid4' ? 'active' : ''}`}
                                    onClick={() => setLayout('grid4')}
                                    title="In 4 đơn trên 1 tờ A4 (Lưới 2x2) - Tối ưu cắt giấy nhất"
                                >
                                    <i className="fa-solid fa-table-cells"></i> 4 đơn / tờ A4 (Khuyên dùng)
                                </button>
                                <button
                                    type="button"
                                    className={`batch-print-toggle-btn ${layout === 'grid2' ? 'active' : ''}`}
                                    onClick={() => setLayout('grid2')}
                                    title="In 2 đơn trên 1 tờ A4 (Khổ A5) - Rộng rãi cho đơn lớn"
                                >
                                    <i className="fa-solid fa-table-columns"></i> 2 đơn / tờ A4
                                </button>
                            </div>
                        </div>

                        {/* Color Mode Toggle */}
                        <div className="batch-print-field">
                            <span>Chế độ:</span>
                            <div className="batch-print-toggle-pills">
                                <button
                                    type="button"
                                    className={`batch-print-toggle-btn ${colorMode === 'bw' ? 'active' : ''}`}
                                    onClick={() => setColorMode('bw')}
                                    title="In đen trắng tương phản cao - Chuẩn máy in Laser & tiết kiệm mực"
                                >
                                    <i className="fa-solid fa-circle-half-stroke"></i> Đen trắng (Laser)
                                </button>
                                <button
                                    type="button"
                                    className={`batch-print-toggle-btn ${colorMode === 'color' ? 'active' : ''}`}
                                    onClick={() => setColorMode('color')}
                                    title="In màu / Kem nhạt"
                                >
                                    <i className="fa-solid fa-palette"></i> Màu
                                </button>
                            </div>
                        </div>

                        {/* QR Mode Toggle */}
                        {showQr && (
                            <div className="batch-print-field">
                                <span>Mã QR:</span>
                                <div className="batch-print-toggle-pills">
                                    <button
                                        type="button"
                                        className={`batch-print-toggle-btn ${qrMode === 'unpaid_only' ? 'active' : ''}`}
                                        onClick={() => setQrMode('unpaid_only')}
                                        title="Chỉ đơn CHƯA thanh toán / COD mới in mã VietQR thu tiền; đơn ĐÃ thanh toán ẩn phần này"
                                    >
                                        <i className="fa-solid fa-stamp"></i> Chỉ đơn chưa thanh toán (Khuyên dùng)
                                    </button>
                                    <button
                                        type="button"
                                        className={`batch-print-toggle-btn ${qrMode === 'always_vietqr' ? 'active' : ''}`}
                                        onClick={() => setQrMode('always_vietqr')}
                                        title="Luôn in mã VietQR MBBank cho mọi đơn hàng"
                                    >
                                        <i className="fa-solid fa-qrcode"></i> Luôn hiện VietQR
                                    </button>
                                    <button
                                        type="button"
                                        className={`batch-print-toggle-btn ${qrMode === 'lookup' ? 'active' : ''}`}
                                        onClick={() => setQrMode('lookup')}
                                        title="In mã QR tra cứu đơn hàng web cho toàn bộ các đơn"
                                    >
                                        <i className="fa-solid fa-magnifying-glass"></i> Tra cứu web
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Options checkboxes */}
                    <div className="batch-print-options-wrap">
                        <label className="batch-print-checkbox-label">
                            <input
                                type="checkbox"
                                checked={showCutLines}
                                onChange={(e) => setShowCutLines(e.target.checked)}
                            />
                            <span>Đường cắt ✂</span>
                        </label>
                        <label className="batch-print-checkbox-label">
                            <input
                                type="checkbox"
                                checked={showSubCakes}
                                onChange={(e) => setShowSubCakes(e.target.checked)}
                            />
                            <span>Vị bánh trong hộp</span>
                        </label>
                        <label className="batch-print-checkbox-label">
                            <input
                                type="checkbox"
                                checked={showQr}
                                onChange={(e) => setShowQr(e.target.checked)}
                            />
                            <span>Mã QR</span>
                        </label>
                        <label className="batch-print-checkbox-label">
                            <input
                                type="checkbox"
                                checked={showCustomerNote}
                                onChange={(e) => setShowCustomerNote(e.target.checked)}
                            />
                            <span>Ghi chú khách</span>
                        </label>
                    </div>

                    {/* Actions */}
                    <div className="batch-print-actions">
                        {!singleBill && filteredBills.length > 0 && (
                            <button
                                type="button"
                                className="batch-print-toggle-btn"
                                onClick={handleToggleAll}
                                style={{ background: '#EAE0D2' }}
                            >
                                {selectedBillIds.length === filteredBills.length ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn-batch-print-now"
                            onClick={handlePrint}
                            disabled={billsToPrint.length === 0}
                        >
                            <i className="fa-solid fa-print"></i>
                            In ngay ({billsToPrint.length} đơn / {pages.length} trang)
                        </button>
                    </div>
                </div>

                {/* ORDERS CHIPS SELECTOR BAR */}
                {!singleBill && filteredBills.length > 0 && (
                    <div className="batch-print-orders-bar no-print">
                        <span style={{ fontSize: '11.5px', fontWeight: 700, color: '#6E5D4F', whiteSpace: 'nowrap' }}>
                            Đơn sẽ in ({selectedBillIds.length}/{filteredBills.length}):
                        </span>
                        <div className="batch-print-orders-chips">
                            {filteredBills.map(b => (
                                <button
                                    key={b.id}
                                    type="button"
                                    className={`batch-order-chip ${selectedBillIds.includes(b.id) ? 'active' : ''}`}
                                    onClick={() => handleToggleBill(b.id)}
                                    title={`${b.customerName} - ${b.customerPhone || ''} (Bấm để bật/tắt in đơn này)`}
                                >
                                    <i className={`fa-solid ${selectedBillIds.includes(b.id) ? 'fa-square-check' : 'fa-square'}`}></i>
                                    #{b.orderCode} ({b.customerName || 'Khách'})
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* PREVIEW & PRINT CANVAS */}
                <div className="batch-print-preview-area">
                    {billsToPrint.length === 0 ? (
                        <div className="batch-print-empty-state no-print">
                            <i className="fa-solid fa-receipt"></i>
                            <h4>Không tìm thấy đơn hàng nào phù hợp</h4>
                            <p>Vui lòng thay đổi ngày giao hoặc điều kiện lọc trạng thái ở thanh công cụ phía trên.</p>
                        </div>
                    ) : (
                        <div id="batch-bills-print-container" className={colorMode === 'bw' ? 'print-theme-bw' : 'print-theme-color'}>
                            {pages.map((pageBills, pageIdx) => (
                                <div key={`page-${pageIdx}`} className="a4-sheet-container">
                                    <div className="a4-sheet-meta no-print">
                                        <i className="fa-solid fa-file-lines"></i>
                                        <span>Tờ A4 #{pageIdx + 1} / {pages.length} ({layout === 'grid4' ? '4 phiếu / tờ' : '2 phiếu / tờ'})</span>
                                    </div>

                                    <div className={layout === 'grid4' ? 'a4-grid-4' : 'a4-grid-2'}>
                                        {pageBills.map((bill, billIdx) => {
                                            if (!bill) {
                                                // Blank slot to pad layout on last sheet
                                                return (
                                                    <div
                                                        key={`empty-${pageIdx}-${billIdx}`}
                                                        className="print-bill-ticket empty-slot"
                                                    >
                                                        <span>(Khung giấy trống - để cắt rời)</span>
                                                    </div>
                                                );
                                            }

                                            const allItems = parseItems(bill.items);
                                            const density = getDensity(allItems);
                                            // Cat bot neu qua dai, de Tong cong + ma QR luon con cho in
                                            const items = allItems.slice(0, MAX_ITEM_ROWS);
                                            const hiddenItemCount = allItems.length - items.length;
                                            const isPaid = ((bill.paymentStatus || '').toLowerCase() === 'paid' || (bill.status || '').toLowerCase().includes('đã thanh toán')) && !isPartial(bill);
                                            const isPayLater = bill.paymentMethod === 'pay_later';
                                            const isDeposit = bill.paymentMethod === 'deposit_30';
                                            const depositAmount = isDeposit ? Math.round((Number(bill.totalAmount) || 0) * 0.3) : 0;
                                            const remainAmount = isDeposit ? (Number(bill.totalAmount) || 0) - depositAmount : 0;
                                            // Flavour change after payment: what was paid, what is still owed, or owed back.
                                            const partial = isPartial(bill);
                                            const partialPaid = partial ? paidAmountOf(bill) : 0;
                                            const partialDue = partial ? amountDueOf(bill) : 0;
                                            const refundDue = refundDueOf(bill);

                                            return (
                                                <div
                                                    key={bill.id}
                                                    className={`print-bill-ticket density-${density} ${showCutLines ? 'with-cut-lines' : ''}`}
                                                >
                                                    {/* TICKET TOP: BRAND & HEADING */}
                                                    <div>
                                                        <div className="ticket-header">
                                                            <div className="ticket-brand-block">
                                                                <h4>Cookie Labs</h4>
                                                                <div className="ticket-brand-meta">
                                                                    <span>Hotline: <strong>0856.345.789</strong></span>
                                                                    <span>·</span>
                                                                    <span>order.cookielabs.vn</span>
                                                                </div>
                                                            </div>
                                                            <div className="ticket-type-badge">
                                                                Hoá Đơn Bán Hàng
                                                            </div>
                                                        </div>

                                                        {/* ORDER BAR */}
                                                        <div className="ticket-order-bar">
                                                            <div className="ticket-order-code">
                                                                #{bill.orderCode}
                                                            </div>
                                                            <div className="ticket-delivery-time">
                                                                {bill.delivery_date ? formatDateShort(bill.delivery_date) : 'Chưa định ngày'}
                                                                {bill.deliveryTimeSlot ? ` (${bill.deliveryTimeSlot})` : ''}
                                                            </div>
                                                        </div>

                                                        {/* CUSTOMER INFO */}
                                                        <div className="ticket-customer-block">
                                                            <div className="ticket-customer-primary">
                                                                <span className="ticket-customer-name">
                                                                    {bill.customerName || 'Khách nhận bánh'}
                                                                </span>
                                                                <span className="ticket-customer-phone">
                                                                    {bill.customerPhone || 'Chưa có SĐT'}
                                                                </span>
                                                            </div>
                                                            <div className="ticket-customer-address" title={bill.customerAddress}>
                                                                <strong>Đ/c:</strong> {bill.customerAddress || 'Nhận tại cửa hàng'}
                                                            </div>
                                                        </div>

                                                    </div>

                                                    {/* ITEMS TABLE */}
                                                    <div className="ticket-items-container">
                                                        <table className="ticket-items-table">
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ width: '46%' }}>Sản phẩm</th>
                                                                    <th style={{ width: '8%', textAlign: 'center' }}>SL</th>
                                                                    <th style={{ width: '21%', textAlign: 'right' }}>Đơn giá</th>
                                                                    <th style={{ width: '25%', textAlign: 'right' }}>Thành tiền</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {items.map((it, itIdx) => {
                                                                    const rawSubs = Array.isArray(it.items) ? it.items : [];
                                                                    // Bỏ danh sách vị bánh khi nó chỉ lặp lại tên sản phẩm (bánh lẻ)
                                                                    const subCakes = isRedundantSubList(it.name, rawSubs)
                                                                        ? []
                                                                        : rawSubs;
                                                                    const qty = it.qty || 1;
                                                                    const price = it.total || (it.price ? it.price * qty : 0);
                                                                    const unitPrice = it.price || (qty ? Math.round(price / qty) : price);

                                                                    return (
                                                                        <tr key={it.id || itIdx}>
                                                                            <td>
                                                                                <div className="ticket-item-name">{it.name}</div>
                                                                                {showSubCakes && subCakes.length > 0 && (
                                                                                    <div className="ticket-subcakes-pills">
                                                                                        {subCakes.map((c, cIdx) => (
                                                                                            <span key={cIdx} className="ticket-subcake-item">
                                                                                                {cIdx > 0 && <span className="ticket-subcake-sep"> · </span>}
                                                                                                <strong className="ticket-subcake-idx">{cIdx + 1}.</strong> {c.name}
                                                                                            </span>
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                                {showSubCakes && it.itemsSummary && rawSubs.length === 0 && (
                                                                                    <div className="ticket-subcakes-pills">
                                                                                        {it.itemsSummary}
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                            <td className="ticket-item-qty">{qty}</td>
                                                                            <td className="ticket-item-unit">{formatPlain(unitPrice)}</td>
                                                                            <td className="ticket-item-price">{formatPlain(price)}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                                {hiddenItemCount > 0 && (
                                                                    <tr className="ticket-more-row">
                                                                        <td colSpan={4}>
                                                                            … và {hiddenItemCount} mục khác
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* FOOTER & TOTAL */}
                                                    <div className="ticket-bottom">
                                                        {/* Bang cong tien: tach khoi bang san pham de
                                                            KHONG BAO GIO bi cat khi hoa don qua dai.
                                                            Dung lai dung cac be rong cot cho thang hang. */}
                                                        <table className="ticket-totals-table">
                                                            <colgroup>
                                                                <col style={{ width: '46%' }} />
                                                                <col style={{ width: '8%' }} />
                                                                <col style={{ width: '21%' }} />
                                                                <col style={{ width: '25%' }} />
                                                            </colgroup>
                                                            <tbody>
                                                                <tr className="ticket-sum-row">
                                                                    <td colSpan={3}>Tạm tính</td>
                                                                    <td>{formatPlain(bill.totalAmount)}</td>
                                                                </tr>
                                                                {isDeposit && (
                                                                    <tr className="ticket-sum-row">
                                                                        <td colSpan={3}>Đã cọc 30%</td>
                                                                        <td>&minus;{formatPlain(depositAmount)}</td>
                                                                    </tr>
                                                                )}
                                                                {partial && (
                                                                    <tr className="ticket-sum-row">
                                                                        <td colSpan={3}>Đã thanh toán</td>
                                                                        <td>&minus;{formatPlain(partialPaid)}</td>
                                                                    </tr>
                                                                )}
                                                                <tr className="ticket-sum-row total">
                                                                    <td colSpan={3}>Tổng cộng</td>
                                                                    <td>{formatCurrency(bill.totalAmount)}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>

                                                        {/* Trang thai thanh toan: dat canh bang cong tien,
                                                            dung cho cua hoa don - ca cau chuyen tien nam mot cho */}
                                                        <div
                                                            className={`ticket-payment-banner ${isPaid ? 'paid' : (isDeposit || partial) ? 'deposit' : 'pay_later'
                                                                }`}
                                                        >
                                                            <div>
                                                                {isPaid ? (
                                                                    <span>{refundDue > 0 ? 'Đã thanh toán · hoàn lại khách' : 'Đã thanh toán'}</span>
                                                                ) : partial ? (
                                                                    <span>Còn thu chênh lệch đổi vị</span>
                                                                ) : isPayLater ? (
                                                                    <span>Cần thanh toán khi nhận</span>
                                                                ) : isDeposit ? (
                                                                    <span>Còn phải thanh toán</span>
                                                                ) : (
                                                                    <span>Cần thanh toán</span>
                                                                )}
                                                            </div>
                                                            <div>
                                                                {isPaid ? (
                                                                    <span>{refundDue > 0 ? formatCurrency(refundDue) : '0 đ'}</span>
                                                                ) : (
                                                                    <span className="cod-badge">
                                                                        {formatCurrency(partial ? partialDue : isDeposit ? remainAmount : bill.totalAmount)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* CUSTOMER NOTE */}
                                                        {showCustomerNote && bill.comment_user && (
                                                            <div className="ticket-note-box">
                                                                <span><strong>Ghi chú:</strong> {bill.comment_user}</span>
                                                            </div>
                                                        )}

                                                        {showQr && (() => {
                                                            const qrInfo = getQrData(bill);
                                                            if (!qrInfo) return null;

                                                            return (
                                                                <div className="ticket-footer-row">
                                                                    <div className="ticket-qr-block">
                                                                        <img
                                                                            src={qrInfo.url}
                                                                            alt={qrInfo.title}
                                                                            className="ticket-qr-img"
                                                                            crossOrigin="anonymous"
                                                                            loading="eager"
                                                                        />
                                                                        <div className="ticket-qr-label">
                                                                            {qrInfo.type === 'vietqr' ? (
                                                                                <>
                                                                                    <span className="qr-type-name">Quét mã để chuyển khoản</span>
                                                                                    <span className="qr-sub-info">{qrInfo.title}</span>
                                                                                    <span className="qr-sub-info">{qrInfo.subtitle}</span>
                                                                                    <span className="qr-app-hint">
                                                                                        {sepayConfig.accountHolder || 'VU THE HAI'}
                                                                                    </span>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <span className="qr-type-name">{qrInfo.title}</span>
                                                                                    <span className="qr-sub-info">{qrInfo.subtitle}</span>
                                                                                </>
                                                                            )}
                                                                            <span className="qr-thanks">
                                                                                Cảm ơn Quý khách đã ủng hộ Cookie Labs!
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
